import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { observer } from "mobx-react-lite";
import { type FC, useEffect, useState } from "react";

import { toastError } from "@/components/molecules";
import { useMenuActions } from "@/components/units/BrandFormMenu/menuActions/MenuActionsContext";
import type { DishFormType } from "@/components/units/BrandFormMenu/types";

import AddDish from "./AddDish";
import SortableDishItem from "./SortableDishItem";

interface DishesBlockProps {
  positions: DishFormType[];
  onEditClick: (dish: DishFormType) => void;
  onCreateClick: () => void;
  category: string;
  hasUpdatePermission?: boolean;
}

const DishesBlock: FC<DishesBlockProps> = observer(
  ({
    positions,
    onCreateClick,
    onEditClick,
    category,
    hasUpdatePermission = true,
  }) => {
    const menuActions = useMenuActions();
    const [items, setItems] = useState<DishFormType[]>(positions);

    const sensors = useSensors(useSensor(PointerSensor));

    const handleDragEnd = async (event: DragEndEvent) => {
      const { active, over } = event;
      if (active.id !== over?.id) {
        const oldIndex = items.findIndex(i => i.id === active.id);
        const newIndex = items.findIndex(i => i.id === over?.id);

        const newItems = arrayMove(items, oldIndex, newIndex);
        setItems(newItems);

        const orderedIds = newItems.map(d => d.id || "");
        try {
          await menuActions.reorderDishes(category, orderedIds);
          await menuActions.invalidateMenuQuery();
        } catch (error) {
          setItems(positions);
          toastError(
            error instanceof Error ? error.message : "Failed to reorder dishes",
            () => {},
            "Error"
          );
        }
      }
    };

    useEffect(() => {
      setItems(positions);
    }, [positions]);

    return (
      <div className="rounded-md border border-gray-200 ml-10 mb-3">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={items.map(i => i.id || "")}
            strategy={verticalListSortingStrategy}
          >
            <ul>
              {items.map(elem => (
                <SortableDishItem
                  key={elem.id}
                  item={elem}
                  category={category}
                  onClick={() => onEditClick(elem)}
                  hasUpdatePermission={hasUpdatePermission}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
        <AddDish
          classes="rounded-t-[0px] border-none"
          onClick={onCreateClick}
          hasUpdatePermission={hasUpdatePermission}
        />
      </div>
    );
  }
);

export default DishesBlock;
